import { ArrowDownTrayIcon, BanknotesIcon, MagnifyingGlassIcon, PencilSquareIcon } from '@heroicons/react/20/solid';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Button, Column, DataTable, Field, InlineBanner, Input, notify, PageHeader, RowAction, Select } from '../../components/ui';
import { useRemote } from '../../features/home/useRemote';
import { useApiError } from '../../hooks/useApiError';
import { usePageTitle } from '../../hooks/usePageTitle';
import api from '../../services/api';
import { FeeRateDialog, FeeRateTarget } from './FeeRateDialog';
import { formatMoney, formatMonth, formatRate } from './format';
import type { AdminStatementRow, AdminStatements } from './types';

const MONTHS_BACK = 36;
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** The month `back` months before the current UTC month, as 'YYYY-MM'. */
const monthsAgo = (back: number): string => {
    const now = new Date();
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - back, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

interface Total {
    currency: string;
    orders: number;
    value: number;
    fee: number;
    plans: number;
}

/** Cents-exact sums per currency: fees and values in the venue currency, plan prices in the price currency. Currencies are never mixed. */
const totalsByCurrency = (rows: AdminStatementRow[]): Total[] => {
    const map = new Map<string, Total>();
    const slot = (currency: string): Total => {
        const found = map.get(currency) ?? { currency, orders: 0, value: 0, fee: 0, plans: 0 };
        map.set(currency, found);
        return found;
    };
    const cents = (n: number) => Math.round(n * 100);
    rows.forEach((r) => {
        const own = slot(r.currency);
        own.orders += r.completedOrders;
        own.value += cents(r.completedValue);
        own.fee += cents(r.fee);
        slot(r.planPriceCurrency).plans += cents(r.planPrice);
    });
    return [...map.values()]
        .map((x) => ({ ...x, value: x.value / 100, fee: x.fee / 100, plans: x.plans / 100 }))
        .sort((a, b) => a.currency.localeCompare(b.currency));
};

/** Admin: every restaurant's statement for a month, CSV download and fee-rate editing. */
const StatementsPage = () => {
    const { t, i18n } = useTranslation('billing');
    usePageTitle(t('statements.title'));
    const apiError = useApiError();
    const lang = i18n.language;
    const [params, setParams] = useSearchParams();
    const lastClosed = monthsAgo(1);
    const monthParam = params.get('month');
    const month = monthParam && MONTH_RE.test(monthParam) ? monthParam : lastClosed;
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState<FeeRateTarget | null>(null);
    const [downloading, setDownloading] = useState(false);

    const statements = useRemote(`admin-statements:${month}`, () =>
        api.get<AdminStatements>('/admin/statements', { params: { month } }).then((r) => r.data)
    );

    const monthOptions = useMemo(
        () => Array.from({ length: MONTHS_BACK }, (_, i) => monthsAgo(i)).map((m) => ({ value: m, label: formatMonth(m, lang) })),
        [lang]
    );
    const options = monthOptions.some((o) => o.value === month) ? monthOptions : [...monthOptions, { value: month, label: formatMonth(month, lang) }];

    const rows = useMemo(() => {
        const needle = search.trim().toLocaleLowerCase(lang);
        const all = statements.data?.rows ?? [];
        return needle ? all.filter((r) => r.restaurantName.toLocaleLowerCase(lang).includes(needle)) : all;
    }, [statements.data, search, lang]);
    const totals = useMemo(() => totalsByCurrency(rows), [rows]);

    const money = (amount: number, currency: string) => formatMoney(amount, currency, lang);

    const download = async () => {
        setDownloading(true);
        try {
            const { data } = await api.get<Blob>('/admin/statements.csv', { params: { month }, responseType: 'blob' });
            const url = URL.createObjectURL(data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `qckorder-statements-${month}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            notify.error(apiError(err));
        } finally {
            setDownloading(false);
        }
    };

    const columns: Column<AdminStatementRow>[] = [
        {
            id: 'restaurant',
            header: t('statements.restaurant'),
            priority: 'primary',
            sortValue: (r) => r.restaurantName,
            cell: (r) => <span className="font-semibold text-strong">{r.restaurantName}</span>,
        },
        { id: 'plan', header: t('statements.plan'), priority: 'secondary', sortValue: (r) => r.plan, cell: (r) => t(`statements.planName.${r.plan}`) },
        { id: 'planPrice', header: t('statements.planPrice'), priority: 'meta', align: 'end', sortValue: (r) => r.planPrice, cell: (r) => money(r.planPrice, r.planPriceCurrency) },
        { id: 'orders', header: t('statements.orders'), priority: 'meta', align: 'end', sortValue: (r) => r.completedOrders, cell: (r) => r.completedOrders },
        { id: 'value', header: t('statements.value'), priority: 'meta', align: 'end', sortValue: (r) => r.completedValue, cell: (r) => money(r.completedValue, r.currency) },
        { id: 'rate', header: t('statements.rate'), priority: 'meta', align: 'end', sortValue: (r) => r.feeRate, cell: (r) => formatRate(r.feeRate, lang) },
        { id: 'fee', header: t('statements.fee'), priority: 'meta', align: 'end', sortValue: (r) => r.fee, cell: (r) => money(r.fee, r.currency) },
        {
            id: 'total',
            header: t('statements.total'),
            priority: 'meta',
            align: 'end',
            sortValue: (r) => r.total ?? -1,
            cell: (r) => (r.total === null ? '–' : money(r.total, r.planPriceCurrency)),
        },
        { id: 'currency', header: t('statements.currency'), priority: 'meta', sortValue: (r) => r.currency, cell: (r) => r.currency },
    ];

    const totalColumns: Column<Total>[] = [
        { id: 'currency', header: t('statements.currency'), priority: 'primary', cell: (x) => <span className="font-semibold text-strong">{x.currency}</span> },
        { id: 'orders', header: t('statements.orders'), priority: 'meta', align: 'end', cell: (x) => x.orders },
        { id: 'value', header: t('statements.value'), priority: 'meta', align: 'end', cell: (x) => money(x.value, x.currency) },
        { id: 'fee', header: t('statements.fee'), priority: 'meta', align: 'end', cell: (x) => money(x.fee, x.currency) },
        { id: 'plans', header: t('statements.planPrices'), priority: 'meta', align: 'end', cell: (x) => money(x.plans, x.currency) },
        { id: 'sum', header: t('statements.invoiced'), priority: 'meta', align: 'end', cell: (x) => money((Math.round(x.fee * 100) + Math.round(x.plans * 100)) / 100, x.currency) },
    ];

    return (
        <div>
            <PageHeader
                title={t('statements.title')}
                meta={t('statements.meta')}
                actions={
                    <Button variant="outline" icon={<ArrowDownTrayIcon className="size-5" aria-hidden="true" />} loading={downloading} onClick={download}>
                        {t('statements.download')}
                    </Button>
                }
            />
            <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:max-w-2xl">
                <Field label={t('statements.month')}>
                    {(c) => (
                        <Select
                            {...c}
                            value={month}
                            options={options}
                            onChange={(m) =>
                                setParams(
                                    (prev) => {
                                        const next = new URLSearchParams(prev);
                                        next.delete('page');
                                        if (m === lastClosed) next.delete('month');
                                        else next.set('month', m);
                                        return next;
                                    },
                                    { replace: true }
                                )
                            }
                        />
                    )}
                </Field>
                <Field label={t('statements.search')}>
                    {(c) => (
                        <Input
                            {...c}
                            type="search"
                            value={search}
                            autoComplete="off"
                            leading={<MagnifyingGlassIcon className="size-5" aria-hidden="true" />}
                            placeholder={t('statements.searchPlaceholder')}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    )}
                </Field>
            </div>
            {statements.data && !statements.data.closed && <InlineBanner tone="info" className="mb-4">{t('statements.running')}</InlineBanner>}
            <DataTable
                rows={rows}
                columns={columns}
                getRowId={(r) => r.restaurantId}
                state={statements.state}
                onRetry={statements.reload}
                caption={t('statements.caption', { month: formatMonth(month, lang) })}
                tableFrom="xl"
                rowLabel={(r) => r.restaurantName}
                rowActions={(r): RowAction[] => [
                    { id: 'rate', label: t('statements.editRate'), icon: PencilSquareIcon, onSelect: () => setEditing({ id: r.restaurantId, name: r.restaurantName }) },
                ]}
                empty={{ icon: BanknotesIcon, title: search ? t('statements.noMatch') : t('statements.empty') }}
            />
            {statements.state === 'ready' && totals.length > 0 && (
                <section aria-labelledby="statement-totals" className="mt-6">
                    <h2 id="statement-totals" className="mb-3 text-title text-strong">
                        {t('statements.totals')}
                    </h2>
                    <DataTable
                        rows={totals}
                        columns={totalColumns}
                        getRowId={(x) => x.currency}
                        state="ready"
                        caption={t('statements.totals')}
                        pageParam="totalsPage"
                        tableFrom="xl"
                        empty={{ icon: BanknotesIcon, title: t('statements.empty') }}
                    />
                    <p className="mt-2 text-caption text-muted">{t('statements.totalsNote')}</p>
                </section>
            )}
            {editing && <FeeRateDialog target={editing} onClose={() => setEditing(null)} onSaved={statements.reload} />}
        </div>
    );
};

export default StatementsPage;
